import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useInput } from 'react-admin';

import { apiGet } from '@src/services/api/helpers';
import { deduplicateByEmail } from '@src/utils/scheduling';

const DEFAULT_VALUE = [];
const filter = createFilterOptions();

const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim());
};

const splitEmails = (text) => {
    if (!text || typeof text !== 'string') return [];
    return text
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter((e) => isValidEmail(e));
};

const RecipientsInputStandalone = ({
    value = DEFAULT_VALUE,
    onChange,
    onBlur,
    error = false,
    helperText,
    label = 'Recipients',
    placeholder = 'Add recipient',
    disabled = false,
    required = false,
    fullWidth = true,
}) => {
    const recipients = Array.isArray(value) ? value : [];
    const [adminUsers, setAdminUsers] = useState([]);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchedAdmins, setSearchedAdmins] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;

        apiGet('/users?access_level[]=admin&perPage=100')
            .then((res) => {
                if (isMountedRef.current) {
                    const users = res.data?.users || res.data || [];
                    setAdminUsers(deduplicateByEmail(Array.isArray(users) ? users : []));
                }
            })
            .catch(() => {
                if (isMountedRef.current) {
                    setAdminUsers([]);
                }
            });

        return () => {
            isMountedRef.current = false;
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const addCustomEmails = (text) => {
        const newEmails = splitEmails(text);

        if (newEmails.length > 0) {
            const combined = deduplicateByEmail([...recipients, ...newEmails]);

            if (onChange) {
                onChange(combined);
            }

            setSearchInputValue('');

            return true;
        }

        return false;
    };

    const handleRecipientsInputChange = (_event, newInputValue, reason) => {
        if (reason === 'reset') return;

        if (newInputValue.includes(',') || newInputValue.includes(';')) {
            addCustomEmails(newInputValue);
            return;
        }

        setSearchInputValue(newInputValue);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        const trimmed = newInputValue.trim();

        if (trimmed.length < 2) {
            setSearchedAdmins([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        searchTimeoutRef.current = setTimeout(() => {
            apiGet(`/users?access_level[]=admin&query=${encodeURIComponent(trimmed)}&perPage=50`)
                .then((res) => {
                    if (isMountedRef.current) {
                        const users = res.data?.users || res.data || [];
                        setSearchedAdmins(deduplicateByEmail(Array.isArray(users) ? users : []));
                    }
                })
                .catch(() => {
                    if (isMountedRef.current) {
                        setSearchedAdmins([]);
                    }
                })
                .finally(() => {
                    if (isMountedRef.current) {
                        setIsSearching(false);
                    }
                });
        }, 300);
    };

    const handleKeyDown = (e) => {
        if (['Enter', ',', ';', 'Tab'].includes(e.key)) {
            if (searchInputValue.trim() && isValidEmail(searchInputValue.trim())) {
                e.preventDefault();
                addCustomEmails(searchInputValue);
            }
        }
    };

    const handlePaste = (e) => {
        const pastedData = e.clipboardData?.getData('text');

        if (pastedData && (pastedData.includes(',') || pastedData.includes(';') || pastedData.includes('\n') || pastedData.includes(' '))) {
            if (addCustomEmails(pastedData)) {
                e.preventDefault();
            }
        }
    };

    const handleBlur = (e) => {
        if (searchInputValue.trim()) {
            if (isValidEmail(searchInputValue.trim())) {
                addCustomEmails(searchInputValue);
            } else {
                setSearchInputValue('');
            }
        }

        if (onBlur) {
            onBlur(e);
        }
    };

    const recipientBase = searchInputValue.trim().length >= 2 ? searchedAdmins : adminUsers;
    const currentRecipientOptions = deduplicateByEmail([...recipients, ...recipientBase]);

    return (
        <Autocomplete
            multiple
            freeSolo
            selectOnFocus
            clearOnBlur={false}
            fullWidth={fullWidth}
            loading={isSearching}
            options={currentRecipientOptions}
            value={recipients}
            disabled={disabled}
            inputValue={searchInputValue}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const trimmed = inputValue.trim();

                if (trimmed !== '' && isValidEmail(trimmed)) {
                    const isExisting = options.some((option) => {
                        const email = (typeof option === 'string' ? option : option?.email || '').trim().toLowerCase();
                        return email === trimmed.toLowerCase();
                    });

                    if (!isExisting) {
                        filtered.push(trimmed);
                    }
                }

                return filtered;
            }}
            getOptionKey={(option) => {
                if (typeof option === 'string') {
                    return `str-${option.trim().toLowerCase()}`;
                }

                const email = (option.email || '').trim().toLowerCase();
                return email ? `admin-${email}` : `admin-id-${option.id}`;
            }}
            getOptionLabel={(option) => {
                if (typeof option === 'string') return option;

                const name = (option.first_name || option.last_name)
                    ? `${option.first_name || ''} ${option.last_name || ''}`.trim()
                    : (option.name && option.name !== option.email ? option.name : null);

                return name ? `${name} (${option.email})` : (option.email || option.name || '');
            }}
            isOptionEqualToValue={(option, val) => {
                const optEmail = (typeof option === 'string' ? option : option?.email || '').trim().toLowerCase();
                const valEmail = (typeof val === 'string' ? val : val?.email || '').trim().toLowerCase();

                if (optEmail && valEmail) {
                    return optEmail === valEmail;
                }

                if (typeof option === 'object' && typeof val === 'object' && option?.id && val?.id) {
                    return option.id === val.id;
                }

                return false;
            }}
            renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                const isUser = typeof option === 'object';
                const name = isUser && ((option.first_name || option.last_name)
                    ? `${option.first_name || ''} ${option.last_name || ''}`.trim()
                    : (option.name && option.name !== option.email ? option.name : null));
                const email = isUser ? (option.email || option.name) : option;
                const optKey = typeof option === 'string'
                    ? `str-${option.trim().toLowerCase()}`
                    : (option.email ? `admin-${option.email.trim().toLowerCase()}` : `admin-id-${option.id}`);

                return (
                    <li key={key || optKey} {...otherProps}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                            {name ? (
                                <>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {email}
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="body2">{email}</Typography>
                            )}
                        </Box>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    error={Boolean(error)}
                    helperText={helperText ?? 'Pick admin users or type any email address. Each recipient gets the download link when the report is ready.'}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isSearching ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                />
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    const isUser = typeof option === 'object' && Boolean(option.id || option.first_name || option.last_name);
                    const chipLabel = typeof option === 'string'
                        ? option
                        : (option.email || `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.name || '');

                    return (
                        <Chip
                            key={key || (typeof option === 'string' ? option : option.email || index)}
                            size="small"
                            icon={isUser ? <PersonOutlineIcon fontSize="small" /> : undefined}
                            label={chipLabel}
                            {...tagProps}
                        />
                    );
                })
            }
            onInputChange={handleRecipientsInputChange}
            onChange={(_e, newValue) => {
                const expanded = [];
                (newValue || []).forEach((item) => {
                    if (typeof item === 'string') {
                        const splitted = splitEmails(item);

                        if (splitted.length > 0) {
                            expanded.push(...splitted);
                        }
                    } else {
                        expanded.push(item);
                    }
                });
                const uniqueList = deduplicateByEmail(expanded);

                if (onChange) {
                    onChange(uniqueList);
                }

                setSearchInputValue('');
            }}
            onBlur={handleBlur}
        />
    );
};

RecipientsInputStandalone.propTypes = {
    value: PropTypes.array,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    helperText: PropTypes.node,
    label: PropTypes.node,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    fullWidth: PropTypes.bool,
};

RecipientsInputStandalone.defaultProps = {
    value: undefined,
    onChange: undefined,
    onBlur: undefined,
    error: false,
    helperText: undefined,
    label: 'Recipients',
    placeholder: 'Add recipient',
    disabled: false,
    required: false,
    fullWidth: true,
};

const RecipientsInputWithSource = (props) => {
    const {
        field,
        fieldState: { error },
        isRequired,
    } = useInput(props);

    return (
        <RecipientsInputStandalone
            {...props}
            required={props.required || isRequired}
            value={field.value}
            error={Boolean(error)}
            helperText={error?.message || props.helperText}
            onChange={(newValue) => field.onChange(newValue)}
            onBlur={field.onBlur}
        />
    );
};

RecipientsInputWithSource.propTypes = {
    required: PropTypes.bool,
    helperText: PropTypes.node,
};

RecipientsInputWithSource.defaultProps = {
    required: false,
    helperText: undefined,
};

const RecipientsInput = (props) => {
    if (props.source) {
        return <RecipientsInputWithSource {...props} />;
    }

    return <RecipientsInputStandalone {...props} />;
};

RecipientsInput.propTypes = {
    source: PropTypes.string,
    value: PropTypes.array,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    helperText: PropTypes.node,
    label: PropTypes.node,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    fullWidth: PropTypes.bool,
};

RecipientsInput.defaultProps = {
    source: undefined,
    value: undefined,
    onChange: undefined,
    onBlur: undefined,
    error: false,
    helperText: undefined,
    label: 'Recipients',
    placeholder: 'Add recipient',
    disabled: false,
    required: false,
    fullWidth: true,
};

export default RecipientsInput;
